import React, { FC, useState } from 'react'
import { useForm, SubmitErrorHandler } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers'
import ControlledPhoneInput from 'transfix-form/components/ControlledPhoneInput'
import { DeprecatedControlledTextInput } from 'transfix-form/components/ControlledTextInput'
import { Box, Typography } from 'transfix-ui/core'
import * as yup from 'yup'
import { Checkbox } from 'components/forms'
import { Button } from 'transfix-ui'
import { postMessageToMobileApp } from 'utils/mobileApp'
import { validatePassword } from 'utils/validators'

const requiredMessage = 'This field is required.'
const phoneMessage = 'Please enter a 10-digit phone number.'

const PhoneSchema = yup.string().nullable().min(10, phoneMessage).max(10, phoneMessage)

export const NewUserSchema = yup.object({
  firstName: yup.string().nullable().required(requiredMessage).default(''),
  lastName: yup.string().nullable().required(requiredMessage).default(''),
  password: yup
    .string()
    .required(requiredMessage)
    .test(
      'is-valid-password',
      'Your password should be at least 8 characters long, and contain at least one uppercase letter, lowercase letter, number, and a special character (!?@#$).',
      validatePassword
    )
    .default(''),
  phone: PhoneSchema.required(requiredMessage),
  cellPhone: PhoneSchema.required(requiredMessage),
})

export type NewUserSchemaType = yup.InferType<typeof NewUserSchema>

export interface ISignupFormProps {
  isLoading?: boolean
  error?: string
  defaultValues?: Partial<NewUserSchemaType>
  onSuccess: (data: NewUserSchemaType) => void
  onError: SubmitErrorHandler<NewUserSchemaType>
  /**
   * Query param set by mobile app webview
   * @default false
   */
  isMobileApp?: boolean
}

/**
 * Sign up form for FP Onboarding Wizard
 */
export const NewUserForm: FC<ISignupFormProps> = ({
  defaultValues = {},
  isMobileApp = false,
  onSuccess: onSuccessProp,
  onError: onErrorProp,
  isLoading,
}) => {
  const [isPasswordVisible, setPasswordVisible] = useState<boolean>(false)
  const { control, errors: formErrors, handleSubmit } = useForm({
    mode: 'all',
    resolver: yupResolver(NewUserSchema),
    shouldFocusError: true,
    defaultValues: {
      ...NewUserSchema.cast({
        ...defaultValues,
      }),
    },
  })

  const checkboxChangeHandler = () => setPasswordVisible(prev => !prev)

  const submitHandler = handleSubmit(onSuccessProp, onErrorProp)

  const isSubmitDisabled = !!Object.keys({ ...formErrors }).length || isLoading

  const termsConditionsMobileHandler = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    if (isMobileApp) {
      event.preventDefault()
      postMessageToMobileApp(isMobileApp, 'PRESSED_TERMS_OF_SERVICE_LINK')
    }
  }

  const privacyPolicyMobileHandler = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    if (isMobileApp) {
      event.preventDefault()
      postMessageToMobileApp(isMobileApp, 'PRESSED_PRIVACY_POLICY_LINK')
    }
  }

  return (
    <form onSubmit={submitHandler} data-test='new-user-form'>
      <Box mb={2}>
        <DeprecatedControlledTextInput
          control={control}
          name='firstName'
          TextInputProps={{
            dataTest: 'input-firstName',
            clearable: false,
            label: 'First Name',
            placeholder: 'Jane',
            large: true,
            validationMessage: formErrors?.firstName?.message,
            invalid: !!formErrors?.firstName?.message,
            autoFocus: true,
          }}
        />
      </Box>
      <Box mb={2}>
        <DeprecatedControlledTextInput
          control={control}
          name='lastName'
          TextInputProps={{
            dataTest: 'input-lastName',
            clearable: false,
            label: 'Last Name',
            placeholder: 'Marie',
            large: true,
            validationMessage: formErrors?.lastName?.message,
            invalid: !!formErrors?.lastName?.message,
          }}
        />
      </Box>
      <Box mb={2}>
        <ControlledPhoneInput
          control={control}
          name='phone'
          PhoneInputProps={{
            dataTest: 'input-phone',
            clearable: false,
            label: 'Phone',
            placeholder: '(555) 123-5678',
            large: true,
            validationMessage:
              formErrors?.phone?.message ||
              'We ask for this in case we need to contact you about your shipments.',
            invalid: !!formErrors?.phone?.message,
            className: 'fs-mask',
          }}
        />
      </Box>
      <Box mb={2}>
        <ControlledPhoneInput
          control={control}
          name='cellPhone'
          PhoneInputProps={{
            dataTest: 'input-cellPhone',
            clearable: false,
            label: 'Mobile phone',
            placeholder: '(555) 123-5678',
            large: true,
            validationMessage:
              formErrors?.cellPhone?.message ||
              'We ask for this to authenticate you and to contact you about your shipments.',
            invalid: !!formErrors?.cellPhone?.message,
            className: 'fs-mask',
          }}
        />
      </Box>
      <Box mb={2}>
        <DeprecatedControlledTextInput
          control={control}
          name='password'
          TextInputProps={{
            dataTest: 'input-password',
            clearable: false,
            label: 'Enter your password',
            placeholder: 'Enter a secure password',
            large: true,
            validationMessage: formErrors?.password?.message,
            helperMessage:
              'Pick an 8-character length password that has at least one uppercase letter, lowercase letter, a number and a special character (!?@#$).',
            invalid: !!formErrors?.password?.message,
            type: isPasswordVisible ? 'text' : 'password',
            className: 'fs-exclude',
          }}
        />
        <Box mt={1}>
          <Checkbox label='Show password' onChange={checkboxChangeHandler} />
        </Box>
      </Box>
      <Box pt={1} mb={2}>
        <Button
          color='primary'
          onClick={submitHandler}
          type='submit'
          size='large'
          fullWidth
          data-test='continue-button'
          disabled={isSubmitDisabled}>
          Continue
        </Button>
      </Box>
      <Box pb={2} textAlign='center'>
        <Typography variant='caption' color='textSecondary' component='div'>
          By clicking &quot;Continue&quot; you agree to Transfix&apos;s{' '}
          <a
            href='https://transfix.io/terms-of-service/'
            target='_blank'
            rel='noopener noreferrer'
            onClick={termsConditionsMobileHandler}>
            Terms and Conditions
          </a>
          {' and '}
          <a
            href='https://transfix.io/privacy-policy/'
            target='_blank'
            rel='noopener noreferrer'
            onClick={privacyPolicyMobileHandler}>
            Privacy Policy
          </a>
        </Typography>
      </Box>
    </form>
  )
}
